import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Calendar,
  Plus,
  Edit3,
  Power,
  CheckCircle2,
  Ban,
  CreditCard,
  Building2,
  Filter,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime, todayIsoDate } from '@/lib/format';

type AmenityKind =
  | 'clubhouse'
  | 'gym'
  | 'party_hall'
  | 'conference_room'
  | 'garden'
  | 'swimming_pool'
  | 'badminton_court'
  | 'guest_room'
  | 'other';

type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
type PaymentStatus = 'not_required' | 'pending' | 'paid' | 'refunded';

interface Amenity {
  id: string;
  name: string;
  kind: AmenityKind;
  description: string | null;
  capacity: number;
  slot_minutes: number;
  open_hour: number;
  close_hour: number;
  fee_inr: number;
  advance_days: number;
  max_active_per_resident: number;
  cancellable_until_hours: number;
  is_active: boolean;
  rules: string | null;
}

interface Booking {
  id: string;
  amenity_id: string;
  resident_id: string;
  starts_at: string;
  ends_at: string;
  guest_count: number;
  notes: string | null;
  status: BookingStatus;
  payment_status: PaymentStatus;
  fee_inr: number;
  amenity?: { id: string; name: string; kind: AmenityKind; fee_inr: number };
  resident?: {
    id: string;
    full_name: string;
    phone: string;
    unit: { id: string; number: string; block: { id: string; name: string } | null };
  };
}

const KIND_LABEL: Record<AmenityKind, string> = {
  clubhouse: 'Clubhouse',
  gym: 'Gym',
  party_hall: 'Party hall',
  conference_room: 'Conference room',
  garden: 'Garden',
  swimming_pool: 'Swimming pool',
  badminton_court: 'Badminton court',
  guest_room: 'Guest room',
  other: 'Other'
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'No show'
};

const STATUS_VARIANT: Record<BookingStatus, 'info' | 'ok' | 'alert' | 'muted' | 'warn'> = {
  pending: 'warn',
  confirmed: 'info',
  cancelled: 'muted',
  completed: 'ok',
  no_show: 'alert'
};

const PAY_LABEL: Record<PaymentStatus, string> = {
  not_required: 'Free',
  pending: 'Pending',
  paid: 'Paid',
  refunded: 'Refunded'
};

const PAY_VARIANT: Record<PaymentStatus, 'info' | 'ok' | 'alert' | 'muted' | 'warn'> = {
  not_required: 'muted',
  pending: 'warn',
  paid: 'ok',
  refunded: 'muted'
};

const KIND_OPTIONS: AmenityKind[] = [
  'clubhouse',
  'gym',
  'party_hall',
  'conference_room',
  'garden',
  'swimming_pool',
  'badminton_court',
  'guest_room',
  'other'
];

const FormSchema = z.object({
  name: z.string().trim().min(2, 'Name required').max(120),
  kind: z.enum([
    'clubhouse',
    'gym',
    'party_hall',
    'conference_room',
    'garden',
    'swimming_pool',
    'badminton_court',
    'guest_room',
    'other'
  ]),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  capacity: z.coerce.number().int().min(1).max(1000),
  slot_minutes: z.coerce.number().int().min(15).max(720),
  open_hour: z.coerce.number().int().min(0).max(23),
  close_hour: z.coerce.number().int().min(1).max(24),
  fee_inr: z.coerce.number().int().min(0).max(100000),
  advance_days: z.coerce.number().int().min(0).max(180),
  max_active_per_resident: z.coerce.number().int().min(1).max(50),
  cancellable_until_hours: z.coerce.number().int().min(0).max(168),
  rules: z.string().trim().max(1000).optional().or(z.literal(''))
});
type FormValues = z.infer<typeof FormSchema>;

const DEFAULT_FORM: FormValues = {
  name: '',
  kind: 'clubhouse',
  description: '',
  capacity: 1,
  slot_minutes: 60,
  open_hour: 6,
  close_hour: 22,
  fee_inr: 0,
  advance_days: 14,
  max_active_per_resident: 2,
  cancellable_until_hours: 2,
  rules: ''
};

type BookingFilter = 'today' | 'week' | 'pending_payment' | 'cancelled' | 'all';

function formatInr(n: number): string {
  return `₹${n.toLocaleString('en-IN')}`;
}

function unitLabel(b: Booking): string {
  if (!b.resident?.unit) return '—';
  const blk = b.resident.unit.block?.name ?? '—';
  return `${blk} / ${b.resident.unit.number}`;
}

function residentLabel(b: Booking): string {
  return b.resident?.full_name ?? '—';
}

export function AmenitiesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'amenities' | 'bookings'>('amenities');
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);

  // ─── Amenities list ───
  const amenities = useQuery({
    queryKey: ['amenities'],
    queryFn: () => get<{ items: Amenity[] }>('/amenities')
  });
  const amenityList = amenities.data?.items ?? [];

  // ─── Bookings list ───
  const [filter, setFilter] = useState<BookingFilter>('today');
  const dateRange = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(startOfDay.getTime() + 24 * 60 * 60 * 1000);
    const endOfWeek = new Date(startOfDay.getTime() + 7 * 24 * 60 * 60 * 1000);
    return { startOfDay, endOfDay, endOfWeek };
  }, []);

  const bookingsFiltersHash = useMemo(() => JSON.stringify([filter]), [filter]);
  const [bookingsCursor, setBookingsCursor] = useState<string | null>(null);
  useEffect(() => {
    setBookingsCursor(null);
  }, [bookingsFiltersHash]);
  const bookingsQuery = useQuery({
    queryKey: ['amenity-bookings', filter, bookingsCursor],
    queryFn: () => {
      const params: Record<string, string | number> = { limit: 50 };
      if (filter === 'today') {
        params.date_from = dateRange.startOfDay.toISOString();
        params.date_to = dateRange.endOfDay.toISOString();
      } else if (filter === 'week') {
        params.date_from = dateRange.startOfDay.toISOString();
        params.date_to = dateRange.endOfWeek.toISOString();
      } else if (filter === 'pending_payment') {
        params.payment_status = 'pending';
      } else if (filter === 'cancelled') {
        params.status = 'cancelled';
      }
      if (bookingsCursor) params.cursor = bookingsCursor;
      return get<{ items: Booking[]; next_cursor: string | null }>('/bookings', { params });
    },
    enabled: tab === 'bookings'
  });
  const bookingsPaginator = useCursorAccumulator<Booking>(bookingsQuery.data, bookingsCursor, bookingsFiltersHash);
  const bookings = bookingsPaginator.items;

  // ─── Add / edit dialog ───
  const [editing, setEditing] = useState<Amenity | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: DEFAULT_FORM
  });

  const openCreate = () => {
    setEditing(null);
    form.reset(DEFAULT_FORM);
    setDialogOpen(true);
  };
  const openEdit = (a: Amenity) => {
    setEditing(a);
    form.reset({
      name: a.name,
      kind: a.kind,
      description: a.description ?? '',
      capacity: a.capacity,
      slot_minutes: a.slot_minutes,
      open_hour: a.open_hour,
      close_hour: a.close_hour,
      fee_inr: a.fee_inr,
      advance_days: a.advance_days,
      max_active_per_resident: a.max_active_per_resident,
      cancellable_until_hours: a.cancellable_until_hours,
      rules: a.rules ?? ''
    });
    setDialogOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: (v: FormValues) => {
      const body = {
        name: v.name,
        kind: v.kind,
        ...(v.description ? { description: v.description } : { description: '' }),
        capacity: v.capacity,
        slot_minutes: v.slot_minutes,
        open_hour: v.open_hour,
        close_hour: v.close_hour,
        fee_inr: v.fee_inr,
        advance_days: v.advance_days,
        max_active_per_resident: v.max_active_per_resident,
        cancellable_until_hours: v.cancellable_until_hours,
        ...(v.rules ? { rules: v.rules } : { rules: '' })
      };
      if (editing) return patch<Amenity>(`/amenities/${editing.id}`, body);
      return post<Amenity>('/amenities', body);
    },
    onSuccess: () => {
      toast.success(editing ? 'Amenity updated' : 'Amenity created');
      setDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['amenities'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleActive = useMutation({
    mutationFn: async (a: Amenity) => {
      if (a.is_active) {
        await del<void>(`/amenities/${a.id}`);
        return;
      }
      await patch<Amenity>(`/amenities/${a.id}`, { is_active: true });
    },
    onSuccess: () => {
      toast.success('Amenity updated');
      void queryClient.invalidateQueries({ queryKey: ['amenities'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── Booking mutations ───
  const markPaid = useMutation({
    mutationFn: (id: string) => post<Booking>(`/bookings/${id}/mark-paid`, {}),
    onSuccess: () => {
      toast.success('Booking marked paid');
      void queryClient.invalidateQueries({ queryKey: ['amenity-bookings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const markCompleted = useMutation({
    mutationFn: (id: string) => post<Booking>(`/bookings/${id}/mark-completed`, {}),
    onSuccess: () => {
      toast.success('Booking marked completed');
      void queryClient.invalidateQueries({ queryKey: ['amenity-bookings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const cancel = useMutation({
    mutationFn: (id: string) => post<Booking>(`/bookings/${id}/cancel`, {}),
    onSuccess: () => {
      toast.success('Booking cancelled');
      void queryClient.invalidateQueries({ queryKey: ['amenity-bookings'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onSubmit = (v: FormValues) => {
    if (v.close_hour <= v.open_hour) {
      form.setError('close_hour', { message: 'Must be greater than open hour' });
      return;
    }
    saveMutation.mutate(v);
  };

  const formDialog = (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogTrigger asChild>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Add amenity
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit amenity' : 'Add amenity'}</DialogTitle>
          <DialogDescription>
            Configure how residents can book this facility.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-vt-4">
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-name">Name</Label>
              <Input id="amty-name" placeholder="Community clubhouse" {...form.register('name')} />
              {form.formState.errors.name ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {form.formState.errors.name.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-kind">Type</Label>
              <Select id="amty-kind" {...form.register('kind')}>
                {KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="amty-desc">Description</Label>
            <Textarea id="amty-desc" rows={2} {...form.register('description')} />
          </div>

          <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-capacity">Capacity</Label>
              <Input id="amty-capacity" type="number" min={1} {...form.register('capacity')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-slot">Slot (min)</Label>
              <Input id="amty-slot" type="number" min={15} step={15} {...form.register('slot_minutes')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-open">Open hour</Label>
              <Input id="amty-open" type="number" min={0} max={23} {...form.register('open_hour')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-close">Close hour</Label>
              <Input id="amty-close" type="number" min={1} max={24} {...form.register('close_hour')} />
              {form.formState.errors.close_hour ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {form.formState.errors.close_hour.message}
                </p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-fee">Fee (₹)</Label>
              <Input id="amty-fee" type="number" min={0} {...form.register('fee_inr')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-advance">Advance days</Label>
              <Input id="amty-advance" type="number" min={0} {...form.register('advance_days')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-max">Max / resident</Label>
              <Input id="amty-max" type="number" min={1} {...form.register('max_active_per_resident')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="amty-cancel">Cancel hr</Label>
              <Input
                id="amty-cancel"
                type="number"
                min={0}
                {...form.register('cancellable_until_hours')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="amty-rules">Usage rules (shown to resident)</Label>
            <Textarea id="amty-rules" rows={3} {...form.register('rules')} />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveMutation.isPending}
              loadingText="Saving…"
              disabled={saveMutation.isPending || !form.formState.isValid}
            >
              {editing ? 'Save changes' : 'Create amenity'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Amenities"
        description="Manage facilities residents can book — clubhouse, gym, halls."
        actions={tab === 'amenities' ? formDialog : undefined}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as typeof tab)}
              options={[
                { value: 'amenities', label: 'Amenities' },
                { value: 'bookings', label: 'Bookings' }
              ]}
              aria-label="Amenities sections"
            />
            <span className="ml-auto text-vt-12 text-vt-text-muted">
              {tab === 'amenities'
                ? `${amenityList.length} amenit${amenityList.length === 1 ? 'y' : 'ies'}`
                : `${bookings.length} booking${bookings.length === 1 ? '' : 's'}`}
            </span>
          </CardContent>
        </Card>

        {tab === 'amenities' ? (
          // ─── Amenities tab ───
          amenities.isLoading ? (
            <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-44 w-full" />
              ))}
            </div>
          ) : amenities.isError ? (
            <EmptyState
              icon={<Calendar className="h-6 w-6" />}
              title="Could not load amenities"
              description={getErrorMessage(amenities.error)}
              action={
                <Button variant="secondary" onClick={() => void amenities.refetch()}>
                  Try again
                </Button>
              }
            />
          ) : amenityList.length === 0 ? (
            <EmptyState
              icon={<Calendar className="h-6 w-6" />}
              title="No amenities yet"
              description="Create your first amenity (clubhouse, gym, hall) to allow residents to book."
              action={
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> Add amenity
                </Button>
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
              {amenityList.map((a) => (
                <Card key={a.id}>
                  <CardContent className="space-y-vt-3 p-vt-4">
                    <div className="flex items-start gap-vt-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                        <Building2 className="h-5 w-5" strokeWidth={1.75} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-vt-2">
                          <h3 className="truncate text-vt-16 font-semibold text-vt-text">{a.name}</h3>
                          <Badge variant={a.is_active ? 'ok' : 'muted'}>
                            {a.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="mt-vt-1 text-vt-12 text-vt-text-muted">
                          {KIND_LABEL[a.kind]} · capacity {a.capacity} · {a.slot_minutes}-min slots
                        </div>
                        {a.description ? (
                          <p className="mt-vt-2 line-clamp-2 text-vt-13 text-vt-text-muted">
                            {a.description}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-vt-2 text-vt-12 text-vt-text-muted md:grid-cols-4">
                      <div>
                        <div className="text-vt-text-soft">Hours</div>
                        <div className="font-medium text-vt-text">
                          {a.open_hour}:00–{a.close_hour}:00
                        </div>
                      </div>
                      <div>
                        <div className="text-vt-text-soft">Fee</div>
                        <div className="font-medium text-vt-text">
                          {a.fee_inr > 0 ? formatInr(a.fee_inr) : 'Free'}
                        </div>
                      </div>
                      <div>
                        <div className="text-vt-text-soft">Advance</div>
                        <div className="font-medium text-vt-text">{a.advance_days}d</div>
                      </div>
                      <div>
                        <div className="text-vt-text-soft">Cancel by</div>
                        <div className="font-medium text-vt-text">
                          {a.cancellable_until_hours}h prior
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-vt-2">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(a)}>
                        <Edit3 className="h-4 w-4" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={a.is_active ? 'ghost' : 'secondary'}
                        disabled={toggleActive.isPending}
                        onClick={() => toggleActive.mutate(a)}
                      >
                        <Power className="h-4 w-4" /> {a.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : (
          // ─── Bookings tab ───
          <>
            <Card>
              <CardContent className="flex flex-wrap items-center gap-vt-2 p-vt-4">
                <Filter className="h-4 w-4 text-vt-text-muted" />
                <span className="text-vt-12 text-vt-text-muted">Filter:</span>
                <TabsPills
                  size="sm"
                  value={filter}
                  onChange={(v) => setFilter(v as BookingFilter)}
                  options={[
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'This week' },
                    { value: 'pending_payment', label: 'Pending payment' },
                    { value: 'cancelled', label: 'Cancelled' },
                    { value: 'all', label: 'All' }
                  ]}
                  aria-label="Booking filter"
                />
                <span className="ml-auto text-vt-12 text-vt-text-muted">
                  {filter === 'today'
                    ? `Today · ${formatDate(todayIsoDate())}`
                    : filter === 'week'
                      ? 'Next 7 days'
                      : null}
                </span>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-vt-4">
                {bookingsQuery.isLoading ? (
                  <div className="flex flex-col gap-vt-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : bookingsQuery.isError ? (
                  <EmptyState
                    icon={<Calendar className="h-6 w-6" />}
                    title="Could not load bookings"
                    description={getErrorMessage(bookingsQuery.error)}
                    action={
                      <Button variant="secondary" onClick={() => void bookingsQuery.refetch()}>
                        Try again
                      </Button>
                    }
                  />
                ) : bookings.length === 0 ? (
                  <EmptyState
                    icon={<Calendar className="h-6 w-6" />}
                    title="No bookings"
                    description="No bookings match the current filter."
                  />
                ) : (
                  <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Amenity</TableHead>
                        <TableHead>Resident</TableHead>
                        <TableHead>Slot</TableHead>
                        <TableHead className="text-right">Guests</TableHead>
                        <TableHead className="text-right">Fee</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow key={b.id}>
                          <TableCell>
                            <div className="flex items-center gap-vt-3">
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                                <Building2 className="h-4 w-4" />
                              </span>
                              <div className="min-w-0">
                                <div className="font-medium text-vt-text">
                                  {b.amenity?.name ?? '—'}
                                </div>
                                <div className="text-vt-12 text-vt-text-muted">
                                  {b.amenity ? KIND_LABEL[b.amenity.kind] : '—'}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-vt-text">{residentLabel(b)}</div>
                            <div className="text-vt-12 text-vt-text-muted">{unitLabel(b)}</div>
                          </TableCell>
                          <TableCell className="text-vt-text-muted">
                            {formatDateTime(b.starts_at)}
                            <div className="text-vt-12">→ {formatDateTime(b.ends_at)}</div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="inline-flex items-center gap-1 text-vt-13 text-vt-text">
                              <Users className="h-3.5 w-3.5 text-vt-text-muted" />
                              {b.guest_count}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                            {b.fee_inr > 0 ? formatInr(b.fee_inr) : '—'}
                          </TableCell>
                          <TableCell>
                            <Badge variant={PAY_VARIANT[b.payment_status]}>
                              {PAY_LABEL[b.payment_status]}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[b.status]}>
                              {STATUS_LABEL[b.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="inline-flex items-center gap-vt-1">
                              {b.payment_status === 'pending' ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  title="Mark paid"
                                  disabled={markPaid.isPending}
                                  onClick={() => markPaid.mutate(b.id)}
                                >
                                  <CreditCard className="h-4 w-4" /> Paid
                                </Button>
                              ) : null}
                              {b.status === 'confirmed' || b.status === 'pending' ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Mark completed"
                                    disabled={markCompleted.isPending}
                                    onClick={() => markCompleted.mutate(b.id)}
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    title="Cancel booking"
                                    disabled={cancel.isPending}
                                    onClick={() => setCancelTarget(b)}
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <CursorPaginator
                    hasMore={Boolean(bookingsPaginator.nextCursor)}
                    loading={bookingsQuery.isFetching}
                    itemsShown={bookings.length}
                    pageSize={50}
                    onLoadMore={() => {
                      if (bookingsPaginator.nextCursor) setBookingsCursor(bookingsPaginator.nextCursor);
                    }}
                  />
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(v) => (v ? null : setCancelTarget(null))}
        title="Cancel booking"
        description="Cancel this amenity booking? The resident is notified and the slot opens up for others."
        confirmLabel="Cancel booking"
        cancelLabel="Keep booking"
        variant="warn"
        loading={cancel.isPending}
        onConfirm={() => {
          if (!cancelTarget) return;
          cancel.mutate(cancelTarget.id, { onSuccess: () => setCancelTarget(null) });
        }}
      />
    </>
  );
}
